import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";

const WEEKS = [
    { week: "Week 01", title: "Business and workflow discovery" },
    { week: "Week 02", title: "Opportunity mapping and prioritisation" },
    { week: "Week 03", title: "System design and implementation planning" },
    { week: "Week 04", title: "Roadmap presentation and next-step recommendation" },
];

const DELIVERABLES = [
    "Business workflow assessment",
    "AI opportunity map",
    "Prioritised use cases",
    "90-day implementation roadmap",
    "ROI and impact framing",
];

const OUTCOMES = [
    "A clear view of where AI creates measurable impact",
    "A prioritised set of use cases",
    "A practical 90-day execution plan",
    "Where appropriate, an initial AI-assisted workflow or prototype to demonstrate real-world application",
];

export default function OpportunitySprint() {
    return (
        <div data-testid="page-sprint">
            <Seo
                title="AI Opportunity Sprint | Chief AI Partners"
                description="A structured first step before committing to long-term AI implementation. Discovery, opportunity mapping, system design and roadmap in 2-4 weeks."
                path="/ai-opportunity-sprint"
            />

            <section className="cap-section pt-24 md:pt-32 pb-16" data-testid="sprint-hero">
                <div className="cap-container grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-8">
                        <div className="cap-eyebrow mb-6">AI Opportunity Sprint</div>
                        <h1 className="font-serif text-navy text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
                            A structured 4-week engagement that defines what to implement over the next 90 days.
                        </h1>
                    </div>
                    <div className="lg:col-span-4 lg:pt-4">
                        <p className="text-slate text-lg leading-relaxed">
                            The AI Opportunity Sprint helps your leadership team understand where AI can create real value, what should be prioritised and how implementation should be approached.
                        </p>
                    </div>
                </div>
                <div className="cap-container mt-12"><div className="cap-rule" /></div>
            </section>

            <section className="cap-section" data-testid="sprint-why">
                <div className="cap-container grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <div className="cap-eyebrow mb-4">Why it exists</div>
                        <h2 className="font-serif text-navy text-4xl md:text-5xl leading-[1.05] tracking-tight">
                            Clarity before implementation.
                        </h2>
                    </div>
                    <div className="lg:col-span-7">
                        <p className="text-slate text-lg leading-relaxed">
                            Many businesses jump straight into tools. The result is scattered experimentation and limited adoption. The Sprint creates clarity before implementation, so that investment lands where it creates value.
                        </p>
                    </div>
                </div>
            </section>

            <section className="cap-section bg-navy text-offwhite" data-testid="sprint-weeks">
                <div className="cap-container">
                    <div className="cap-eyebrow mb-4" style={{ color: "#2EA6A1" }}>What happens</div>
                    <h2 className="font-serif text-offwhite text-4xl md:text-5xl leading-[1.05] tracking-tight max-w-3xl">
                        Four weeks. One clear roadmap.
                    </h2>
                    <p className="mt-5 text-offwhite/75 text-lg leading-relaxed max-w-2xl">
                        A structured 4-week engagement that results in a clear 90-day implementation plan.
                    </p>

                    <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {WEEKS.map((w, i) => (
                            <div
                                key={w.week}
                                className="border border-offwhite/20 p-7 min-h-[220px] flex flex-col justify-between"
                                data-testid={`sprint-week-${i}`}
                            >
                                <div className="cap-eyebrow" style={{ color: "#2EA6A1" }}>{w.week}</div>
                                <div className="font-serif text-offwhite text-2xl leading-snug mt-6">{w.title}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="cap-section" data-testid="sprint-deliverables">
                <div className="cap-container">
                    <p
                        className="text-sm md:text-base tracking-[0.08em] text-slate mb-12"
                        data-testid="sprint-progression-line"
                    >
                        4-week Sprint <span className="text-teal">&rarr;</span> 90-day roadmap <span className="text-teal">&rarr;</span> Ongoing implementation
                    </p>
                </div>
                <div className="cap-container grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <div className="cap-eyebrow mb-4">Deliverables</div>
                        <h2 className="font-serif text-navy text-4xl md:text-5xl leading-[1.05] tracking-tight">
                            What you walk away with.
                        </h2>
                        <div className="mt-8 space-y-3 text-sm">
                            <div className="flex flex-wrap items-baseline gap-2">
                                <span className="text-xs uppercase tracking-[0.18em] text-teal font-semibold">Engagement</span>
                                <span className="text-navy font-medium">Structured engagement</span>
                            </div>
                            <div className="flex flex-wrap items-baseline gap-2">
                                <span className="text-xs uppercase tracking-[0.18em] text-teal font-semibold">Scope</span>
                                <span className="text-navy font-medium">Defined scope based on your business</span>
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <ul className="divide-y divide-sand border-y border-sand">
                            {DELIVERABLES.map((d, i) => (
                                <li key={d} className="flex items-start gap-5 py-5" data-testid={`sprint-deliverable-${i}`}>
                                    <span className="cap-numeral text-teal text-2xl min-w-[2.5rem]">{String(i + 1).padStart(2, "0")}</span>
                                    <span className="font-serif text-navy text-2xl leading-snug">{d}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            <section className="cap-section pt-0" data-testid="sprint-outcomes">
                <div className="cap-container grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <div className="cap-eyebrow mb-4">Outcome</div>
                        <h2 className="font-serif text-navy text-4xl md:text-5xl leading-[1.05] tracking-tight">
                            What you walk into the next 90 days with.
                        </h2>
                    </div>
                    <div className="lg:col-span-7">
                        <ul className="space-y-4">
                            {OUTCOMES.map((o, i) => (
                                <li
                                    key={o}
                                    className="flex items-start gap-4 border-b border-sand pb-4 last:border-b-0"
                                    data-testid={`sprint-outcome-${i}`}
                                >
                                    <span className="text-xs uppercase tracking-[0.22em] text-teal font-semibold pt-1.5 min-w-[2.5rem]">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <span className="text-navy text-base md:text-lg leading-relaxed">{o}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            <section className="cap-section bg-sand/40 border-y border-sand" data-testid="sprint-final-cta">
                <div className="cap-container text-center max-w-3xl mx-auto">
                    <h2 className="font-serif text-navy text-4xl md:text-5xl leading-[1.05] tracking-tight">
                        Start with a practical conversation.
                    </h2>
                    <p className="mt-6 text-slate text-lg leading-relaxed">
                        Book a 15-minute conversation to discuss whether a Sprint is the right first step for your business.
                    </p>
                    <Link to="/contact" className="cap-btn-primary mt-8" data-testid="sprint-cta-book">
                        Book a 15-minute conversation <ArrowRight size={16} />
                    </Link>
                </div>
            </section>
        </div>
    );
}
